import type { SystemRole } from '@repo/contracts';
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from '@repo/ui';

import { APP_ROUTES } from '@/constants/app-routes';
import { systemRoleLabel } from '@/constants/labels';
import { appRolesOf, offersRoleSwitch } from '@/hooks/auth/app-roles';
import { useSetActiveRole } from '@/hooks/auth/use-set-active-role';
import { toastApiError } from '@/shared/api/toast-api-error';
import { announceSession } from '@/shared/utils/session-channel';
import { selectActiveRole, selectHeldRoles } from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

/**
 * "Switch role" moves between the held roles that belong to this app. It calls the
 * server, which is the only place a role becomes active, so the session and
 * every later request change with it, not just this tab's rendering.
 */
export function SwitchRoleMenu() {
  const activeRole = useAppSelector(selectActiveRole);
  const held = useAppSelector(selectHeldRoles);
  const { mutate: switchRole, isPending } = useSetActiveRole();

  const appRoles = appRolesOf(held);

  if (!offersRoleSwitch(appRoles, activeRole)) return null;

  const selectHeldRole = (role: SystemRole) => {
    if (role === activeRole || isPending) return;

    switchRole(role, {
      // A full page load, not a route change: what a person may see differs
      // by role, and everything fetched under the previous one is still cached
      // and still on screen. Loading the new role's home afresh is the one way
      // to be sure none of it survives, including on the page already open.
      onSuccess: () => {
        // Before the load, so other tabs reload onto the new role too.
        announceSession('role-switched');
        window.location.assign(APP_ROUTES.dashboard);
      },
      onError: toastApiError,
    });
  };

  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        Switch role
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={activeRole ?? undefined}
        onValueChange={(value) => selectHeldRole(value as SystemRole)}
      >
        {appRoles.map((role) => (
          <DropdownMenuRadioItem key={role} value={role}>
            {systemRoleLabel(role)}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>

      <DropdownMenuSeparator />
    </>
  );
}
